import { useMemo, useState } from 'react';
import { Button, DatePicker, Input, Modal, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlertTriangle, CalendarClock, Download, Lock, Wallet } from 'lucide-react';
import * as XLSX from 'xlsx';
import Layout from '../../Layout';
import { ktApi, today, useKt } from './ktStore';
import { debtRows, DEPOSIT_STATE, depositRows, overdueDepositSheet, type DebtRow, type DepositState } from './ktCards';
import { dmy, fmtMoney } from './ktLogic';
import { DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';

const STATE_COLOR: Record<DepositState, string> = { used: 'default', active: 'green', expiring: 'orange', overdue: 'red' };
type DepositRow = ReturnType<typeof depositRows>[number];

/** Cọc có hạn theo chương trình, công nợ gộp theo Mã KH (SRS 2.3) */
export default function KeToanDeposits() {
  const s = useKt();
  const now = today();
  const deposits = useMemo(() => depositRows(s, now), [s, now]);
  const debts = useMemo(() => debtRows(s, now), [s, now]);
  const [extending, setExtending] = useState<DepositRow | null>(null);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  const holding = deposits.filter(r => r.state !== 'used');
  const overdue = deposits.filter(r => r.state === 'overdue');
  const expiring = deposits.filter(r => r.state === 'expiring');
  const locked = debts.filter(r => r.locked);

  const exportOverdue = () => {
    const rows = overdueDepositSheet(s, now);
    if (!rows.length) { message.info('Không có cọc quá hạn'); return; }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Coc qua han');
    XLSX.writeFile(wb, `Nhac_coc_qua_han_${now}.xlsx`);
  };

  const depositColumns: ColumnsType<DepositRow> = [
    { title: 'Mã KH', dataIndex: 'code', width: 110 },
    { title: 'Khách', key: 'c', render: (_, r) => <div><div className="text-gray-900 dark:text-gray-100">{r.bill.customer}</div><div className="text-[11px] text-gray-500">{r.bill.no}, ngày {dmy(r.bill.date)}</div></div> },
    { title: 'Chương trình', key: 'p', render: (_, r) => r.bill.program?.name || <span className="text-gray-400">Theo hạn mặc định {s.depositDays} ngày</span> },
    { title: 'Tiền cọc', dataIndex: 'amount', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Hạn cọc', key: 'u', width: 150, render: (_, r) => (
      <div>
        <div className="text-xs">{dmy(r.until)}{r.bill.depositUntil ? ' (đã gia hạn)' : ''}</div>
        {r.state === 'overdue' && <div className="text-[11px] text-rose-600">Quá {-r.days} ngày</div>}
        {r.state === 'expiring' && <div className="text-[11px] text-amber-700">Còn {r.days} ngày</div>}
      </div>
    ) },
    { title: 'Trạng thái', key: 's', width: 150, render: (_, r) => <div><Tag color={STATE_COLOR[r.state]} className="!mr-0">{DEPOSIT_STATE[r.state]}</Tag>{r.usedBy && <div className="text-[11px] text-gray-500">vào {r.usedBy}</div>}</div> },
    { title: '', key: 'a', width: 90, align: 'right', render: (_, r) => (s.tier === 'chief' && r.state !== 'used'
      ? <Button size="small" onClick={() => { setExtending(r); setUntil(''); setReason(''); }}>Gia hạn</Button>
      : null) },
  ];

  const debtColumns: ColumnsType<DebtRow> = [
    { title: 'Mã KH', dataIndex: 'code', width: 110 },
    { title: 'Khách', key: 'c', render: (_, r) => <div><div className="text-gray-900 dark:text-gray-100">{r.customer}</div><div className="text-[11px] text-gray-500">{r.phone}</div></div> },
    { title: 'Cọc đang giữ', dataIndex: 'deposit', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Trong đó quá hạn', dataIndex: 'overdueDeposit', width: 130, align: 'right', render: (v: number) => (v ? <span className="text-rose-600">{fmtMoney(v)}</span> : fmtMoney(0)) },
    { title: 'Nợ đơn', dataIndex: 'billDebt', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Nợ thẻ MySpa', dataIndex: 'cardDebt', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Chốt chặn', key: 'l', width: 150, render: (_, r) => (r.locked ? <Tag color="red" className="!mr-0">Khóa tạo ca, trừ buổi</Tag> : <Tag className="!mr-0">Không khóa</Tag>) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Cọc và công nợ theo Mã KH" desc="Cọc có hạn theo chương trình bán hàng. Khách còn nợ thì khóa tạo ca và trừ buổi, không có lối duyệt vượt" />
        <DemoBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<Wallet size={20} />} tone="bg-sky-50 text-sky-600" label="Cọc đang giữ" value={fmtMoney(holding.reduce((t, r) => t + r.amount, 0))} hint={`${holding.length} đơn cọc`} />
          <Kpi icon={<AlertTriangle size={20} />} tone="bg-rose-50 text-rose-600" label="Cọc quá hạn" value={overdue.length} hint={fmtMoney(overdue.reduce((t, r) => t + r.amount, 0))} />
          <Kpi icon={<CalendarClock size={20} />} tone="bg-amber-50 text-amber-600" label="Sắp hết hạn" value={expiring.length} hint="Trong 7 ngày tới" />
          <Kpi icon={<Lock size={20} />} tone="bg-violet-50 text-violet-600" label="Khách đang bị khóa" value={locked.length} hint={fmtMoney(locked.reduce((t, r) => t + r.billDebt + r.cardDebt, 0))} />
        </div>

        <Panel title="Sổ cọc và công nợ" extra={<Button size="small" icon={<Download size={13} />} onClick={exportOverdue}>Xuất báo cáo nhắc cọc quá hạn</Button>}>
          <Tabs className="px-4" items={[
            { key: 'deposit', label: `Cọc (${deposits.length})`, children: <div className="pb-4"><Table rowKey={r => r.bill.id} size="middle" columns={depositColumns} dataSource={deposits} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 1000 }} locale={{ emptyText: 'Chưa có đơn cọc' }} /></div> },
            { key: 'debt', label: `Công nợ theo Mã KH (${debts.length})`, children: <div className="pb-4"><Table rowKey={r => `${r.code}${r.customer}`} size="middle" columns={debtColumns} dataSource={debts} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 1000 }} locale={{ emptyText: 'Không có công nợ' }} /></div> },
          ]} />
        </Panel>
      </div>

      <Modal open={!!extending} title={`Gia hạn cọc ${extending?.bill.no}`} okText="Gia hạn" cancelText="Thôi" onCancel={() => setExtending(null)} destroyOnHidden
        onOk={() => { const r = ktApi.extendDeposit(extending!.bill.id, until, reason); if (r.ok) { message.success('Đã gia hạn, thao tác được ghi nhật ký'); setExtending(null); } else showErrors(r.errors); }}>
        <div className="space-y-3">
          <div><label htmlFor="kt-dep-until" className="text-sm font-medium">Hạn mới</label><DatePicker id="kt-dep-until" className="!w-full" format="DD/MM/YYYY" minDate={dayjs(now)} onChange={d => setUntil(d ? d.format('YYYY-MM-DD') : '')} /></div>
          <div><label htmlFor="kt-dep-reason" className="text-sm font-medium">Lý do</label><Input.TextArea id="kt-dep-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} /></div>
        </div>
      </Modal>
    </Layout>
  );
}
